import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { HandHeart, Plus, Loader2, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import api, { bulkUploadVolunteers } from "@/lib/api";
import BulkUploadModal from "@/components/BulkUploadModal";

const get = (url) => api.get(url).then((r) => r.data);
const post = (url, body) => api.post(url, body).then((r) => r.data);

export default function Volunteers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", skills: "" });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = () => get("/api/volunteers").then(setRows);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      setBusy(true);
      await post("/api/volunteers", { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) });
      toast({ title: "Volunteer added" });
      setOpen(false); setForm({ full_name: "", email: "", phone: "", skills: "" });
      load();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const logHours = async (vId) => {  // kept for future use
    const h = window.prompt("Hours to log?");
    if (!h) return;
    try {
      await post(`/api/volunteers/${vId}/hours`, { hours: Number(h) });
      toast({ title: "Hours logged" });
      load();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
  };
  void logHours;

  return (
    <>
      <PageHeader title="Volunteers" subtitle="The hands that turn dollars into impact. Track hours, programs, and skills.">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card text-foreground px-3.5 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            <FileSpreadsheet size={14} className="text-primary" /> Bulk upload
          </button>
          <button onClick={() => setOpen(true)} data-testid="volunteers-new" className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium">
            <Plus size={14} /> Add volunteer
          </button>
        </div>
      </PageHeader>
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        entityType="volunteers"
        onUpload={bulkUploadVolunteers}
        onSuccess={load}
      />
      <PageBody>
        {open && (
          <div className="rounded-xl border border-border bg-card p-5 mb-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" data-testid="volunteers-name" />
              <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
              <input placeholder="Skills (comma)" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="px-3 py-2 rounded border border-border bg-background" />
            </div>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy || !form.full_name} data-testid="volunteers-submit" className="inline-flex items-center gap-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm">
                {busy && <Loader2 size={14} className="animate-spin" />} Save
              </button>
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded border border-border text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.length === 0 && <div className="col-span-3 rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">
            <HandHeart size={28} className="mx-auto mb-2 opacity-30" /> No volunteers yet — add the first.
          </div>}
          {rows.map((v) => (
            <Link key={v.id} to={`/app/volunteers/${v.id}`} className="rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-colors block" data-testid={`volunteer-${v.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">{v.full_name?.charAt(0)}</div>
                <div className="min-w-0">
                  <h4 className="font-medium truncate">{v.full_name}</h4>
                  <p className="text-xs text-muted-foreground truncate">{v.email || v.phone || "—"}</p>
                </div>
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <span className="serif text-2xl text-primary">{Number(v.hours_total || 0).toFixed(0)}</span>
                <span className="text-xs text-muted-foreground">hours logged</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(v.skills || []).map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground">{s}</span>)}
              </div>
              <div className="mt-4 text-xs text-primary under-link">View history →</div>
            </Link>
          ))}
        </div>
      </PageBody>
    </>
  );
}
